import * as React from "react"

import BinanceCsvImportGuidance, {
  BinanceCsvTransactionHistoryWarning,
} from "@/components/Broker/BinanceCsvImportGuidance"
import CtraderStatementImportGuidance from "@/components/Broker/CtraderStatementImportGuidance"
import FuturesOrdersCsvImportGuidance from "@/components/Broker/FuturesOrdersCsvImportGuidance"
import HyperliquidCsvImportGuidance from "@/components/Broker/HyperliquidCsvImportGuidance"
import IbkrFlexSyncGuidance from "@/components/Broker/IbkrFlexSyncGuidance"
import LiquidBrokersStatementImportGuidance from "@/components/Broker/LiquidBrokersStatementImportGuidance"
import OandaTransactionsImportGuidance from "@/components/Broker/OandaTransactionsImportGuidance"
import Mt4StatementImportGuidance from "@/components/Broker/Mt4StatementImportGuidance"
import Mt5TradeHistoryGuidance from "@/components/Broker/Mt5TradeHistoryGuidance"
import TradingViewBacktestGuidance from "@/components/Broker/TradingViewBacktestGuidance"
import TradingViewPaperGuidance from "@/components/Broker/TradingViewPaperGuidance"
import BrokerGuidancePanel, { useBrokerGuidanceColors } from "@/components/Broker/BrokerGuidancePanel"
import {
  ChecklistRow,
  GuidanceAccordionBody,
  GuidanceBlock,
  GuidanceIntro,
} from "@/components/Broker/ibkrGuidanceShared"
import type { BrokerCapabilities } from "@/services/brokerIntegrationApi"
import { isCtraderBroker } from "@/utils/ctraderBroker"
import { isCustomCsvBrokerName } from "@/utils/customCsvAccountDisplay"
import { isFuturesOrdersCsvBroker } from "@/utils/futuresOrdersCsvBroker"
import { isHyperliquidBroker } from "@/utils/hyperliquidBroker"
import { isMt4Broker } from "@/utils/mt4Broker"
import { isMt5Broker } from "@/utils/mt5Broker"
import { resolveBrokerTradeSync } from "@/utils/brokerTradeSync"
import { isTradingViewBroker } from "@/utils/tradingViewBroker"
import { isTradingViewPaperBroker } from "@/utils/tradingViewPaperBroker"
import type { DemoCatalogBroker, DemoParseContract } from "@/types/demoEntryAnalysis"
import { resolveDemoFileFormat } from "@/utils/demoEntryAnalysisBrokers"

function isLiquidBrokersSlug(brokerName: string): boolean {
  const lower = String(brokerName).trim().toLowerCase()
  return lower === "liquidbrokers" || lower === "liquid"
}

function GenericDemoFileGuidance({
  parse,
}: {
  parse?: DemoParseContract | null
}): React.ReactElement {
  const colors = useBrokerGuidanceColors()
  const format = resolveDemoFileFormat(parse)
  return (
    <BrokerGuidancePanel
      title={`How to export a ${format.shortLabel} trade history file`}
      compact
      collapsible
    >
      <GuidanceAccordionBody>
        <GuidanceIntro color={colors.bodyColor} className="!mb-0">
          This broker expects a <strong>{format.shortLabel}</strong> file
          {format.extensions.length ? ` (${format.extensions.join(", ")})` : ""}. Download a
          closed-trade statement from your broker. No API keys.
        </GuidanceIntro>
        <GuidanceBlock title="Demo note">
          <ChecklistRow
            className="!mb-0"
            iconColor={colors.iconColor}
            labelColor={colors.labelColor}
          >
            This demo analyses roughly the last 30 days of closed trades for one instrument.
          </ChecklistRow>
        </GuidanceBlock>
      </GuidanceAccordionBody>
    </BrokerGuidancePanel>
  )
}

function CustomCsvDemoGuidance(): React.ReactElement {
  const colors = useBrokerGuidanceColors()
  return (
    <BrokerGuidancePanel title="How to import a Custom CSV" compact collapsible>
      <GuidanceAccordionBody>
        <GuidanceIntro color={colors.bodyColor} className="!mb-0">
          Upload any broker statement as CSV. Pick the layout that matches your file, then confirm
          the statement timezone.
        </GuidanceIntro>
        <GuidanceBlock title="Things to know">
          <ChecklistRow
            className="!mb-0"
            iconColor={colors.iconColor}
            labelColor={colors.labelColor}
          >
            Use one closed-trade file. Spreadsheets only for this demo.
          </ChecklistRow>
        </GuidanceBlock>
      </GuidanceAccordionBody>
    </BrokerGuidancePanel>
  )
}

/** Same Flex CSV checklist as Broker Management when catalog omits trade_sync. */
function demoIbkrTradeSyncCapability(broker?: DemoCatalogBroker | null): BrokerCapabilities {
  const fromCatalog =
    broker?.trade_sync && typeof broker.trade_sync === "object"
      ? (broker.trade_sync as Record<string, unknown>)
      : null
  return {
    broker_name: "ibkr",
    display_name: broker?.display_name || "Interactive Brokers",
    auth: {
      supports_auto_sync: false,
      required_fields: [],
      optional_fields: [],
    },
    analytics: {},
    csv_import:
      broker?.csv_import && typeof broker.csv_import === "object"
        ? broker.csv_import
        : { supported: true },
    trade_sync: {
      strategy: "flex_statement",
      flex_query_max_lookback_days: 365,
      history_before_flex_query_requires_csv_import: true,
      ...(fromCatalog || {}),
    },
  } as BrokerCapabilities
}

function DemoIbkrCsvGuidance({
  catalogBroker,
}: {
  catalogBroker?: DemoCatalogBroker | null
}): React.ReactElement {
  const tradeSync = resolveBrokerTradeSync(demoIbkrTradeSyncCapability(catalogBroker))
  if (!tradeSync) {
    return <GenericDemoFileGuidance parse={catalogBroker?.demo_parse} />
  }
  return <IbkrFlexSyncGuidance tradeSync={tradeSync} variant="csv_tab" compact />
}

/** Broker-branded how-to copy reused from Broker Management CSV import. */
export function DemoBrokerFileUploadGuidance(props: {
  brokerName: string
  parse?: DemoParseContract | null
  catalogBroker?: DemoCatalogBroker | null
}): React.ReactElement {
  const slug = props.brokerName
  const lower = String(slug).toLowerCase()

  if (isCustomCsvBrokerName(slug)) return <CustomCsvDemoGuidance />
  if (isTradingViewBroker(slug)) {
    return <TradingViewBacktestGuidance compact showAccountNotes={false} />
  }
  if (isTradingViewPaperBroker(slug)) {
    return <TradingViewPaperGuidance compact showAccountNotes={false} />
  }
  if (isMt5Broker(slug)) return <Mt5TradeHistoryGuidance compact />
  if (isMt4Broker(slug)) return <Mt4StatementImportGuidance compact />
  if (isCtraderBroker(slug)) {
    return (
      <CtraderStatementImportGuidance
        compact
        showReimportNote={false}
        showAccountNotes={false}
      />
    )
  }
  if (isFuturesOrdersCsvBroker(slug)) {
    return <FuturesOrdersCsvImportGuidance brokerName={slug} compact />
  }
  if (isLiquidBrokersSlug(slug)) {
    return <LiquidBrokersStatementImportGuidance compact showAccountNotes={false} />
  }
  if (isHyperliquidBroker(slug)) {
    return <HyperliquidCsvImportGuidance compact showAccountNotes={false} />
  }
  if (lower === "oanda") {
    return <OandaTransactionsImportGuidance compact showAccountNotes={false} />
  }
  if (lower === "binance") {
    return (
      <>
        <BinanceCsvImportGuidance />
        <BinanceCsvTransactionHistoryWarning />
      </>
    )
  }
  if (lower === "ibkr") {
    return <DemoIbkrCsvGuidance catalogBroker={props.catalogBroker} />
  }

  return <GenericDemoFileGuidance parse={props.parse} />
}

export function DemoFileFormatHint(props: {
  parse?: DemoParseContract | null
}): React.ReactElement {
  const format = resolveDemoFileFormat(props.parse)
  return (
    <p
      className="font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]"
      data-demo-file-format=""
    >
      Required format: <strong>{format.shortLabel}</strong>
      {format.extensions.length ? ` (${format.extensions.join(", ")})` : ""}
    </p>
  )
}
